import { useState } from "react";
import { CampoNumero, Pestanas, Resultado, Dato, Rejilla, mxn } from "../components/campos.jsx";
import { calcularMargen, calcularRoi, puntoEquilibrio } from "../lib/calc.js";

export default function Negocio() {
  const [modo, setModo] = useState("margen");
  return (
    <div className="space-y-6">
      <Pestanas
        valor={modo}
        onChange={setModo}
        opciones={[
          { valor: "margen", etiqueta: "Márgenes de venta" },
          { valor: "roi", etiqueta: "Retorno de inversión" },
          { valor: "equilibrio", etiqueta: "Punto de equilibrio" },
        ]}
      />
      {modo === "margen" && <Margen />}
      {modo === "roi" && <Roi />}
      {modo === "equilibrio" && <Equilibrio />}
    </div>
  );
}

function Margen() {
  const [costo, setCosto] = useState(100);
  const [precio, setPrecio] = useState(150);
  const r = calcularMargen(costo, precio);

  return (
    <Rejilla>
      <div className="space-y-4">
        <CampoNumero label="Costo unitario del artículo" valor={costo} onChange={setCosto} prefijo="$" min={0} />
        <CampoNumero label="Precio de venta" valor={precio} onChange={setPrecio} prefijo="$" min={0} />
      </div>
      <div className="order-first space-y-3 lg:order-last lg:col-span-2">
        <Resultado
          label="Margen sobre el precio de venta"
          valor={`${r.margenPct.toFixed(2)}%`}
          tono={r.utilidad >= 0 ? "positivo" : "alerta"}
          nota={`Utilidad por pieza: ${mxn(r.utilidad)}`}
        />
        <div className="tarjeta">
          <Dato label="Utilidad bruta" valor={mxn(r.utilidad)} destacar />
          <Dato label="Margen (utilidad ÷ precio)" valor={`${r.margenPct.toFixed(2)}%`} />
          <Dato label="Markup (utilidad ÷ costo)" valor={`${r.markupPct.toFixed(2)}%`} />
        </div>
        <p className="text-2xs texto-3">
          Margen y markup no son lo mismo y confundirlos es el error de precio más común: un markup
          del 50% sobre el costo deja un margen del 33% sobre la venta.
        </p>
      </div>
    </Rejilla>
  );
}

function Roi() {
  const [inversion, setInversion] = useState(50000);
  const [retorno, setRetorno] = useState(75000);
  const [meses, setMeses] = useState(12);
  const r = calcularRoi(inversion, retorno);
  const anualizado = meses > 0 ? (r.roiPct * 12) / meses : 0;

  return (
    <Rejilla>
      <div className="space-y-4">
        <CampoNumero label="Inversión realizada" valor={inversion} onChange={setInversion} prefijo="$" min={0} />
        <CampoNumero label="Retorno obtenido" valor={retorno} onChange={setRetorno} prefijo="$" min={0} />
        <CampoNumero label="Periodo" valor={meses} onChange={setMeses} sufijo="meses" min={1} step="1" />
      </div>
      <div className="order-first space-y-3 lg:order-last lg:col-span-2">
        <Resultado
          label="ROI"
          valor={`${r.roiPct.toFixed(2)}%`}
          tono={r.roiPct >= 0 ? "positivo" : "alerta"}
          nota={`Ganancia neta: ${mxn(r.ganancia)}`}
        />
        <div className="tarjeta">
          <Dato label="Ganancia neta" valor={mxn(r.ganancia)} destacar />
          <Dato label="ROI del periodo" valor={`${r.roiPct.toFixed(2)}%`} />
          <Dato label="ROI anualizado" valor={`${anualizado.toFixed(2)}%`} />
        </div>
      </div>
    </Rejilla>
  );
}

function Equilibrio() {
  const [fijos, setFijos] = useState(50000);
  const [precio, setPrecio] = useState(250);
  const [variable, setVariable] = useState(100);
  const r = puntoEquilibrio(fijos, precio, variable);
  const alcanzable = Number.isFinite(r.unidades);

  return (
    <Rejilla>
      <div className="space-y-4">
        <CampoNumero label="Costos fijos mensuales" valor={fijos} onChange={setFijos} prefijo="$" min={0} />
        <CampoNumero label="Precio de venta unitario" valor={precio} onChange={setPrecio} prefijo="$" min={0} />
        <CampoNumero label="Costo variable por unidad" valor={variable} onChange={setVariable} prefijo="$" min={0} />
      </div>
      <div className="order-first space-y-3 lg:order-last lg:col-span-2">
        {alcanzable ? (
          <>
            <Resultado
              label="Unidades para no perder ni ganar"
              valor={Math.ceil(r.unidades).toLocaleString("es-MX")}
              nota={`Equivale a ${mxn(r.ingresos)} de ventas`}
            />
            <div className="tarjeta">
              <Dato label="Margen de contribución por unidad" valor={mxn(r.margenContribucion)} />
              <Dato label="Unidades en el punto de equilibrio" valor={Math.ceil(r.unidades).toLocaleString("es-MX")} destacar />
              <Dato label="Ingresos en el punto de equilibrio" valor={mxn(r.ingresos)} />
              <Dato label="Ventas diarias necesarias" valor={`${Math.ceil(r.unidades / 30)} piezas`} />
            </div>
          </>
        ) : (
          <div className="rounded-2xl border border-[color:var(--alerta-borde)] bg-[color:var(--alerta-bg)] p-5">
            <p className="text-sm font-semibold text-[color:var(--alerta)]">No hay punto de equilibrio</p>
            <p className="mt-2 text-2xs text-[color:var(--alerta)]">
              Tu costo variable ({mxn(variable)}) es igual o mayor que tu precio de venta ({mxn(precio)}).
              Cada unidad que vendes aumenta la pérdida: ningún volumen de ventas lo compensa.
            </p>
          </div>
        )}
      </div>
    </Rejilla>
  );
}
